import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { usePergunta } from "../contexts/PerguntaContext";
import { appsDaCentral, AppDaCentral } from "../lib/appsDaCentral";
import i18n from "../i18n";
import { rota } from "../lib/rotas";
const { v4: uuidv4 } = require("uuid");

interface SeletorAppProps {
  atual?: AppDaCentral;
}

// Pílula acima do campo de pergunta na tela inicial: mostra o app da conversa e abre uma grade
// com busca para trocar. Faz o mesmo que escolher o app no menu lateral (os dois ficam em
// sincronia pela URL). Ordem fixa (Plus e depois os demais em ordem alfabética): os apps não mudam
// de lugar depois de escolhidos, para a pessoa achar cada um sempre no mesmo ponto.
const SeletorApp: React.FC<SeletorAppProps> = ({ atual }) => {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { setConversaAtual } = usePergunta();
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const raiz = useRef<HTMLDivElement>(null);
  const grade = useRef<HTMLDivElement>(null);
  const idioma = i18n.language;
  const nome = (app: AppDaCentral) => (idioma === "es" ? app.namees : app.name);

  const apps = useMemo(() => appsDaCentral(idioma, busca), [idioma, busca]);

  // Ao abrir, a grade rola até o app atual (só a grade, sem mexer na página).
  useEffect(() => {
    if (!aberto || !grade.current) return;
    const ativo = grade.current.querySelector<HTMLElement>(".seletor-app-opcao.ativo");
    if (ativo && ativo.offsetTop + ativo.offsetHeight > grade.current.clientHeight) {
      grade.current.scrollTop = ativo.offsetTop - grade.current.clientHeight / 2;
    }
  }, [aberto]);

  // Fecha ao clicar fora ou com Esc.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) setBusca("");
  }, [aberto]);

  const escolher = (app: AppDaCentral) => {
    setAberto(false);
    if (app.slugamigavel === atual?.slugamigavel) return;
    setConversaAtual(uuidv4());
    setLocation(rota(`/${app.slugamigavel}/${idioma}`));
  };

  return (
    <div className="seletor-app" ref={raiz}>
      <button
        type="button"
        className="seletor-app-pilula"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-label={t("trocar_app")}
        onClick={() => setAberto((a) => !a)}
      >
        {atual && <span className="seletor-app-icone">{atual.icon}</span>}
        <span>{atual ? nome(atual) : t("escolher_app")}</span>
        <svg className="seletor-app-seta" width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M9.75 4.125L6 7.875L2.25 4.125" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {aberto && (
        <div className="seletor-app-painel">
          <input
            className="seletor-app-busca"
            type="search"
            autoFocus
            placeholder={t("buscar_app")}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && apps[0]) escolher(apps[0]);
            }}
          />
          <div className="seletor-app-grade" role="listbox" ref={grade}>
            {apps.map((app) => (
              <button
                type="button"
                role="option"
                aria-selected={app.slugamigavel === atual?.slugamigavel}
                key={app.slugamigavel}
                className={`seletor-app-opcao${app.slugamigavel === atual?.slugamigavel ? " ativo" : ""}`}
                onClick={() => escolher(app)}
              >
                <span className="seletor-app-icone">{app.icon}</span>
                <span className="seletor-app-nome">{nome(app)}</span>
              </button>
            ))}
            {!apps.length && <p className="seletor-app-vazio">{t("nenhum_app")}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeletorApp;
